import React, { useEffect, useState, useCallback } from 'react';
import { fetchDynamoDBItems } from './api';
import {
  Box,
  CircularProgress,
  Typography,
  Alert,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Link,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
} from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { getNetwork, getExplorerUrls, getFeatures } from './config';
import { useTheme } from './utils/ThemeContext';
import CryptoJS from 'crypto-js';
import LockIcon from '@mui/icons-material/Lock';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import CurrencyBitcoinIcon from '@mui/icons-material/CurrencyBitcoin';
import paystandLogo from './paystand.png'; // Import the Paystand logo

// Constants for validation
const HASH_REGEX = /^0x[a-fA-F0-9]{64}$/;
const BITCOIN_HASH_REGEX = /^[a-fA-F0-9]{64}$/;

// Constants for pin generation
const PIN_SALT = 'state-pin-v1'; // Version info embedded in salt
const PIN_ITERATIONS = 10000;     // PBKDF2 iterations
const PIN_KEY_LENGTH = 32;        // Output key length in bytes

// Helper function to get explorer URL
const getExplorerUrl = (type, hash, network) => {
  if (!hash) return null;
  
  try {
    const baseUrl = getExplorerUrls()[type][network];
    return `${baseUrl}/${hash}`;
  } catch (error) {
    console.error(`Failed to generate explorer URL for ${type}:`, error);
    return null;
  }
};

async function generateSHA256(ethereumHash, previousBitcoinHash, lastPin) {
    // Validate inputs
    if (!ethereumHash) {
        throw new Error('Ethereum hash is required');
    }

    if (!HASH_REGEX.test(ethereumHash)) {
        throw new Error('Invalid Ethereum hash format');
    }

    // For first transaction, both previousBitcoinHash and lastPin should be null
    if (!previousBitcoinHash && lastPin) {
        throw new Error('Invalid state: Cannot have a lastPin without a previousBitcoinHash');
    }

    // Handle first transaction case
    if (lastPin === null || lastPin === undefined) lastPin = '';
    if (previousBitcoinHash === null || previousBitcoinHash === undefined) previousBitcoinHash = '';

    // Validate Bitcoin hash format if provided
    if (previousBitcoinHash && !BITCOIN_HASH_REGEX.test(previousBitcoinHash)) {
        throw new Error('Invalid Bitcoin hash format');
    }

    try {
        // Normalize inputs
        const normalizedEthHash = ethereumHash.toLowerCase().replace('0x', '');
        const normalizedPrevBtcHash = previousBitcoinHash.toLowerCase();

        // Use SubtleCrypto for modern browsers
        if (window.crypto && window.crypto.subtle) {
            // 1. Convert salt to buffer
            const encoder = new TextEncoder();
            const saltBuffer = encoder.encode(PIN_SALT);

            // 2. Generate key using PBKDF2
            const baseKey = await window.crypto.subtle.importKey(
                'raw',
                encoder.encode(lastPin),
                'PBKDF2',
                false,
                ['deriveBits']
            );

            const keyMaterial = await window.crypto.subtle.deriveBits(
                {
                    name: 'PBKDF2',
                    salt: saltBuffer,
                    iterations: PIN_ITERATIONS,
                    hash: 'SHA-256'
                },
                baseKey,
                PIN_KEY_LENGTH * 8
            );

            // 3. Create HMAC key
            const hmacKey = await window.crypto.subtle.importKey(
                'raw',
                keyMaterial,
                { name: 'HMAC', hash: 'SHA-256' },
                false,
                ['sign']
            );

            // 4. Sign the concatenated data
            const data = encoder.encode(normalizedEthHash + normalizedPrevBtcHash);
            const signature = await window.crypto.subtle.sign(
                'HMAC',
                hmacKey,
                data
            );

            // 5. Convert to hex
            const hashArray = Array.from(new Uint8Array(signature));
            return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        } else {
            // Fallback for older browsers using crypto-js
            const keyMaterial = CryptoJS.PBKDF2(lastPin, PIN_SALT, {
                keySize: PIN_KEY_LENGTH / 4,
                iterations: PIN_ITERATIONS,
                hasher: CryptoJS.algo.SHA256
            });

            const hmac = CryptoJS.HmacSHA256(normalizedEthHash + normalizedPrevBtcHash, keyMaterial);
            return hmac.toString();
        }
    } catch (error) {
        console.error('Hash generation failed:', error);
        throw new Error('Failed to generate hash');
    }
}

const Icon = React.memo(({ item, previousItem }) => {
    const [validation, setValidation] = useState(false);
    const [validating, setValidating] = useState(false);
    const [error, setError] = useState(null);

    const validatePin = useCallback(async () => {
        setValidating(true);
        setError(null);
        try {
            // For first transaction
            if (!previousItem) {
                const data = await generateSHA256(
                    item.EthereumHash,
                    null,  // no previous Bitcoin hash
                    null   // no previous pin
                );
                setValidation(data === item.Pin);
                if (data !== item.Pin) {
                    setError('First transaction pin verification failed');
                }
                return;
            }

            // For subsequent transactions
            const data = await generateSHA256(
                item.EthereumHash,
                previousItem.BitcoinHash,
                previousItem.Pin
            );
            setValidation(data === item.Pin);
            if (data !== item.Pin) {
                setError('Chain verification failed - pin doesn\'t match expected value');
            }
        } catch (error) {
            console.error('Pin validation failed:', error);
            setError(error.message);
            setValidation(false);
        } finally {
            setValidating(false);
        }
    }, [item, previousItem]);

    useEffect(() => {
        validatePin();
    }, [validatePin]);

    return (
        <Box 
            display="flex" 
            alignItems="center" 
            sx={{
                p: 1,
                borderRadius: 1
            }}
        >
            <Box flexGrow={1}>
                <Typography 
                    variant="subtitle1" 
                    sx={{ 
                        fontFamily: 'monospace',
                        fontWeight: 'medium',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    Pin: 
                    <span style={{ 
                        backgroundColor: (theme) => theme.palette.grey[100],
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '0.9em'
                    }}>
                        {item.Pin}
                    </span>
                </Typography>
                {previousItem && (
                    <Typography 
                        variant="caption" 
                        color="textSecondary" 
                        sx={{ 
                            display: 'block',
                            mt: 0.5,
                            fontFamily: 'monospace'
                        }}
                    >
                        Previous Pin: {previousItem.BitcoinHash.substring(0, 8)}...
                    </Typography>
                )}
            </Box>
            <Tooltip 
                title={error || (validation ? 'Valid Pin' : 'Invalid Pin')}
                sx={{ ml: 2 }}
            >
                <IconButton 
                    size="small"
                    onClick={validatePin}
                    disabled={validating}
                    sx={{
                        backgroundColor: validation ? 'success.light' : error ? 'error.light' : 'warning.light',
                        color: 'white',
                        '&:hover': {
                            backgroundColor: validation ? 'success.main' : error ? 'error.main' : 'warning.main',
                        }
                    }}
                >
                    {validating ? (
                        <CircularProgress size={20} />
                    ) : validation ? (
                        <VerifiedIcon />
                    ) : (
                        <NewReleasesIcon />
                    )}
                </IconButton>
            </Tooltip>
        </Box>
    );
});

// Paystand logo component
const PaystandLogo = () => {
  const { darkMode } = useTheme();
  
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        marginLeft: 0.5,
      }}
    >
      <Typography
        component="span"
        sx={{
          fontWeight: 500,
          mr: 0.5,
        }}
      >
        Paystand
      </Typography>
      <Box
        component="img"
        src={paystandLogo}
        alt="Paystand"
        sx={{
          width: 18,
          height: 18,
          filter: darkMode ? 'brightness(1.2)' : 'none',
          verticalAlign: 'middle',
        }}
      />
    </Box>
  );
};

const DynamoDBData = () => {
  const { darkMode, toggleDarkMode } = useTheme(); // Get theme and toggle function from context
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const [network, setNetwork] = useState(getNetwork());
  const [chainStatus, setChainStatus] = useState({ valid: true, failedAt: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const verifyChain = useCallback(async (transactions) => {
    if (!transactions || transactions.length === 0) return { valid: true };

    // Sort by timestamp to ensure correct order (newest first)
    const sortedTxs = [...transactions].sort((a, b) => 
      new Date(b.Timedate) - new Date(a.Timedate)
    );

    try {
      // Verify each transaction in sequence
      for (let i = 0; i < sortedTxs.length; i++) {
        const currentTx = sortedTxs[i];
        const previousTx = sortedTxs[i + 1]; // Previous in time (next in array since sorted newest first)

        let expectedPin;
        if (!previousTx) {
          // First transaction (last in array)
          expectedPin = await generateSHA256(currentTx.EthereumHash, null, null);
        } else {
          expectedPin = await generateSHA256(
            currentTx.EthereumHash,
            previousTx.BitcoinHash,
            previousTx.Pin
          );
        }

        if (expectedPin !== currentTx.Pin) {
          return {
            valid: false,
            failedAt: currentTx,
            reason: `Chain verification failed at transaction ${currentTx.Id}`
          };
        }
      }

      return { valid: true };
    } catch (error) {
      console.error('Chain verification failed:', error);
      return {
        valid: false,
        error: error.message
      };
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchDynamoDBItems();
      if (!data || data.length === 0) {
        setError('No transaction data available');
        return;
      }

      // Validate and sanitize data
      const validData = data.filter(item => {
        const validDate = !isNaN(new Date(item.Timedate).getTime());
        const validHashes = HASH_REGEX.test(item.EthereumHash) && 
                          BITCOIN_HASH_REGEX.test(item.BitcoinHash);
        return validDate && validHashes;
      });

      if (validData.length === 0) {
        setError('No valid transaction data available');
        return;
      }

      // Sort items by timestamp in descending order (newest first)
      const sortedItems = validData.sort((a, b) => {
        const dateA = new Date(a.Timedate).getTime();
        const dateB = new Date(b.Timedate).getTime();
        return dateB - dateA;
      });

      setItems(sortedItems);

      // Verify the entire chain
      const status = await verifyChain(sortedItems);
      setChainStatus(status);

    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  }, [verifyChain]);

  const handleError = (err) => {
    if (err.response) {
      switch (err.response.status) {
        case 400:
          setError('Invalid request. Please check your parameters.');
          break;
        case 401:
          setError('Authentication failed. Please check your credentials.');
          break;
        case 404:
          setError('Data not found. The resource might have been moved or deleted.');
          break;
        case 500:
          setError('Server error. Please try again later or contact support.');
          break;
        default:
          setError(`Failed to load data: ${err.response.data?.message || 'Unknown error'}`);
      }
    } else if (err.request) {
      setError('Network error. Please check your connection.');
    } else {
      setError(`Error: ${err.message || 'Unknown error occurred'}`);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData, lastUpdate]);

  const handleRefresh = () => {
    setLastUpdate(Date.now());
  };

  const handleNetworkChange = (event) => {
    setNetwork(event.target.value);
    // Optionally refresh data when network changes
    setLastUpdate(Date.now());
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setSnackbar({ open: true, message: 'Copied to clipboard!' });
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Alert 
          severity="error"
          action={
            <IconButton
              color="inherit"
              size="small"
              onClick={handleRefresh}
            >
              <RefreshIcon />
            </IconButton>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box 
      p={0}
      sx={{
        position: 'relative',
        minHeight: '100vh',
        margin: 0,
        padding: 0,
        background: theme => 
          theme.palette.mode === 'dark' 
            ? `linear-gradient(${theme.palette.background.default}, ${theme.palette.background.default})`
            : `linear-gradient(${theme.palette.background.default}, ${theme.palette.background.default})`,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: theme => theme.palette.mode === 'dark' ? 0.1 : 0.03,
          pointerEvents: 'none',
          backgroundImage: theme => theme.palette.mode === 'dark' 
            ? 'radial-gradient(circle at top right, rgba(99, 102, 241, 0.15), transparent 70%), radial-gradient(circle at bottom left, rgba(139, 92, 246, 0.1), transparent 70%)'
            : `repeating-linear-gradient(
                45deg,
                ${theme.palette.grey[100]},
                ${theme.palette.grey[100]} 2px,
                transparent 2px,
                transparent 10px
              )`,
        },
        '&::after': {
          content: '""',
          display: 'none'
        }
      }}
    >
      {/* Super Loud Header */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          mb: 3,
          py: 2.5,
          px: 2.5,
          mt: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: 1,
          borderColor: theme => theme.palette.mode === 'dark' 
            ? 'rgba(99, 102, 241, 0.5)' 
            : 'rgba(79, 70, 229, 0.3)',
          boxShadow: theme => theme.palette.mode === 'dark' 
            ? '0 4px 20px -2px rgba(0, 0, 0, 0.4), 0 0 20px rgba(99, 102, 241, 0.3)' 
            : '0 4px 15px rgba(0, 0, 0, 0.12)',
          backgroundColor: theme => theme.palette.mode === 'dark' 
            ? 'rgba(15, 23, 42, 0.98)' 
            : 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(16px)',
          borderRadius: 0,
          width: '100%',
          margin: 0,
          marginLeft: 0,
          marginRight: 0,
          marginTop: 0,
          transition: 'all 0.4s ease',
          // Enhanced super-vibrant gradient border
          '&::before': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: theme => theme.palette.mode === 'dark'
              ? 'linear-gradient(90deg, #60a5fa, #818cf8, #a78bfa, #c084fc, #f472b6, #fb7185, #f87171, #fb923c, #fbbf24, #4ade80, #2dd4bf)'
              : 'linear-gradient(90deg, #3b82f6, #4f46e5, #7c3aed, #9333ea, #ec4899, #f43f5e, #ef4444, #f97316, #f59e0b, #22c55e, #14b8a6)',
            opacity: 1,
            backgroundSize: '200% 100%',
            animation: 'headerGradientShift 8s linear infinite',
          },
          // Add subtle left accent
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '15%',
            left: 0,
            width: '5px',
            height: '70%',
            background: theme => theme.palette.mode === 'dark'
              ? 'linear-gradient(to bottom, #60a5fa, #818cf8, #a78bfa)'
              : 'linear-gradient(to bottom, #3b82f6, #4f46e5, #8b5cf6)',
            opacity: 0.9,
            borderTopRightRadius: 4,
            borderBottomRightRadius: 4,
          },
          // Add header shine animation on hover
          '&:hover::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '400%',
            height: '100%',
            background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.15) 40%, transparent 60%)',
            transform: 'skewX(-45deg)',
            animation: 'headerShine 3s ease forwards',
            zIndex: 0,
            pointerEvents: 'none',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LockIcon 
            sx={{ 
              mr: 1.8, 
              fontSize: '1.9rem',
              color: theme => theme.palette.mode === 'dark' ? '#60a5fa' : '#3b82f6',
              filter: theme => theme.palette.mode === 'dark' ? 'drop-shadow(0 0 8px rgba(96, 165, 250, 0.8))' : 'drop-shadow(0 0 4px rgba(59, 130, 246, 0.6))',
              transition: 'transform 0.3s ease, filter 0.3s ease',
              animation: 'pulse 3s infinite ease-in-out',
              '&:hover': {
                transform: 'scale(1.2) rotate(-15deg)',
                filter: theme => theme.palette.mode === 'dark' ? 'drop-shadow(0 0 12px rgba(96, 165, 250, 1))' : 'drop-shadow(0 0 6px rgba(59, 130, 246, 0.9))',
                animation: 'iconVibrate 0.5s infinite ease-in-out',
              }
            }} 
          />
          <Typography 
            variant="h6" 
            component="h1"
            sx={{
              fontWeight: 700,
              color: theme => theme.palette.mode === 'dark' ? '#f1f5f9' : '#1e293b',
              display: 'flex',
              alignItems: 'center',
              flexWrap: { xs: 'wrap', md: 'nowrap' },
              fontSize: { xs: '1.05rem', sm: '1.15rem', md: '1.35rem' },
              textShadow: theme => theme.palette.mode === 'dark' ? '0 0 25px rgba(241, 245, 249, 0.3)' : 'none',
              letterSpacing: '0.5px',
              animation: theme => theme.palette.mode === 'dark' ? 'headerGlow 4s infinite ease-in-out' : 'none',
              position: 'relative',
              '&::before': theme => theme.palette.mode === 'dark' ? {
                content: '""',
                position: 'absolute',
                bottom: '-3px',
                left: '0',
                width: '100%',
                height: '2px',
                background: 'linear-gradient(90deg, transparent, var(--accent-blue), transparent)',
                opacity: 0.6,
              } : {},
            }}
          >
            Anonymous Chain Pinning
            <Typography 
              component="span" 
              sx={{ 
                ml: 1.2,
                color: theme => theme.palette.mode === 'dark' ? '#cbd5e1' : '#475569',
                fontWeight: 600,
                fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1.05rem' },
                display: { xs: 'none', sm: 'inline' },
                position: 'relative',
                '&::before': theme => theme.palette.mode === 'dark' ? {
                  content: '""',
                  position: 'absolute',
                  bottom: '-1px',
                  left: '0',
                  width: '100%',
                  height: '1px',
                  background: 'linear-gradient(90deg, transparent, var(--accent-indigo), transparent)',
                  opacity: 0.4,
                } : {},
              }}
            >
              for Assurety
            </Typography>
            <Box 
              component="span" 
              sx={{ 
                ml: 1.2,
                color: theme => theme.palette.mode === 'dark' ? '#cbd5e1' : '#475569',
                fontWeight: 500,
                fontSize: { xs: '0.85rem', sm: '0.85rem', md: '0.95rem' },
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
              }}
            >
              — Enabled by 
              <CurrencyBitcoinIcon 
                sx={{ 
                  mx: 0.6, 
                  fontSize: '1.3rem',
                  color: '#f7931a', // Bitcoin orange
                  filter: theme => theme.palette.mode === 'dark' ? 'drop-shadow(0 0 6px rgba(247, 147, 26, 0.9))' : 'drop-shadow(0 0 3px rgba(247, 147, 26, 0.6))',
                  transition: 'transform 0.3s ease, filter 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.1)',
                    filter: theme => theme.palette.mode === 'dark' ? 'drop-shadow(0 0 10px rgba(247, 147, 26, 1)) brightness(1.2)' : 'drop-shadow(0 0 6px rgba(247, 147, 26, 0.8))',
                  }
                }} 
              />
              <Typography
                component="span"
                sx={{
                  fontWeight: 600,
                  mr: 0.6,
                  fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1.05rem' },
                  color: theme => theme.palette.mode === 'dark' ? '#f7931a' : '#f7931a',
                  textShadow: theme => theme.palette.mode === 'dark' ? '0 0 16px rgba(247, 147, 26, 0.4)' : '0 0 5px rgba(247, 147, 26, 0.2)',
                }}
              >
                Bitcoin
              </Typography>
              , Powered by <PaystandLogo />
            </Box>
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.7 }}>
          <Tooltip title="Refresh data">
            <IconButton 
              onClick={handleRefresh}
              size="small"
              sx={{
                color: theme => theme.palette.mode === 'dark' ? '#60a5fa' : '#3b82f6', // Colored icon
                background: theme => theme.palette.mode === 'dark' ? 'rgba(30, 41, 59, 0.7)' : 'rgba(241, 245, 249, 0.8)',
                padding: '10px',
                transition: 'all 0.4s cubic-bezier(.47,1.64,.41,.8)',
                border: '2px solid transparent',
                '&:hover': {
                  color: theme => theme.palette.mode === 'dark' ? '#93c5fd' : '#2563eb',
                  background: theme => theme.palette.mode === 'dark' ? 'rgba(51, 65, 85, 0.9)' : 'rgba(241, 245, 249, 1)',
                  transform: 'rotate(360deg) scale(1.15)',
                  boxShadow: theme => theme.palette.mode === 'dark' 
                    ? '0 0 18px rgba(96, 165, 250, 0.6), inset 0 0 10px rgba(96, 165, 250, 0.3)' 
                    : '0 0 12px rgba(59, 130, 246, 0.3)',
                  borderColor: theme => theme.palette.mode === 'dark' ? '#60a5fa' : '#3b82f6',
                }
              }}
            >
              <RefreshIcon fontSize="medium" />
            </IconButton>
          </Tooltip>
          
          <Tooltip title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}>
            <IconButton
              onClick={() => toggleDarkMode()}
              size="small"
              sx={{
                color: theme => theme.palette.mode === 'dark' 
                  ? '#a78bfa' // Purple in dark mode
                  : '#8b5cf6', // Purple in light mode
                background: theme => theme.palette.mode === 'dark' ? 'rgba(30, 41, 59, 0.7)' : 'rgba(241, 245, 249, 0.8)',
                padding: '10px',
                transition: 'all 0.4s cubic-bezier(.47,1.64,.41,.8)',
                border: '2px solid transparent',
                '&:hover': {
                  color: darkMode 
                    ? '#fbbf24' // Yellow/gold in dark mode (representing sun/light)
                    : '#1e293b', // Dark blue/slate in light mode (representing night/dark)
                  background: theme => theme.palette.mode === 'dark' 
                    ? 'rgba(251, 191, 36, 0.2)' // Subtle yellow background in dark mode
                    : 'rgba(30, 41, 59, 0.15)', // Subtle dark background in light mode
                  transform: 'scale(1.15) rotate(20deg)',
                  boxShadow: theme => theme.palette.mode === 'dark' 
                    ? '0 0 18px rgba(251, 191, 36, 0.5), inset 0 0 10px rgba(251, 191, 36, 0.3)' // Yellow glow in dark mode
                    : '0 0 10px rgba(30, 41, 59, 0.4), inset 0 0 5px rgba(30, 41, 59, 0.2)', // Dark glow in light mode
                  borderColor: darkMode ? '#fbbf24' : '#1e293b',
                },
              }}
            >
              {darkMode ? <LightModeIcon fontSize="medium" /> : <DarkModeIcon fontSize="medium" />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box 
        sx={{ 
          position: 'relative',
          zIndex: 1
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          {getFeatures().enableNetworkSwitch && (
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel id="network-select-label">Network</InputLabel>
              <Select
                labelId="network-select-label"
                value={network}
                label="Network"
                onChange={handleNetworkChange}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(71, 85, 105, 0.3)' : undefined,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(99, 102, 241, 0.5)' : undefined,
                  },
                }}
              >
                <MenuItem value="testnet">Testnet</MenuItem>
                <MenuItem value="mainnet">Mainnet</MenuItem>
              </Select>
            </FormControl>
          )}
          <Box display="flex" alignItems="center">
            {!chainStatus.valid && (
              <Alert 
                severity="error" 
                sx={{ 
                  backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(239, 68, 68, 0.2)' : undefined,
                  color: theme => theme.palette.mode === 'dark' ? '#f87171' : undefined,
                  border: theme => theme.palette.mode === 'dark' ? '1px solid rgba(239, 68, 68, 0.3)' : undefined,
                }}
              >
                {chainStatus.reason || chainStatus.error || 'Chain verification failed'}
              </Alert>
            )}
          </Box>
        </Box>
        <Grid container spacing={3} justifyContent="center">
          {items.map((item, index) => (
            <Grid item xs={12} sm={12} md={8} key={item.Id} sx={{ position: 'relative' }}>
              {/* Chain connector - vertical line connecting cards */}
              {index < items.length - 1 && (
                <Box
                  className="chain-connector"
                  sx={{
                    position: 'absolute',
                    left: '50%',
                    top: '100%',
                    bottom: '-24px', // Connects to the next card
                    width: '2px',
                    zIndex: 2,
                    transform: 'translateX(-50%)',
                    background: theme => theme.palette.mode === 'dark' 
                      ? 'linear-gradient(to bottom, var(--accent-indigo), var(--accent-blue))'
                      : 'linear-gradient(to bottom, rgba(99, 102, 241, 0.7), rgba(59, 130, 246, 0.7))',
                    boxShadow: theme => theme.palette.mode === 'dark' 
                      ? '0 0 8px var(--glow-indigo)'
                      : 'none',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: '-5px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      backgroundColor: theme => theme.palette.mode === 'dark' 
                        ? 'var(--accent-indigo)'
                        : 'rgba(99, 102, 241, 0.7)',
                      boxShadow: theme => theme.palette.mode === 'dark' 
                        ? '0 0 8px var(--glow-indigo)'
                        : 'none',
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: '-5px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      backgroundColor: theme => theme.palette.mode === 'dark' 
                        ? 'var(--accent-blue)'
                        : 'rgba(59, 130, 246, 0.7)',
                      boxShadow: theme => theme.palette.mode === 'dark' 
                        ? '0 0 8px var(--glow-blue)'
                        : 'none',
                    },
                  }}
                />
              )}
              
              {/* Pin card with enhanced visual connection */}
              <Card 
                sx={{ 
                  borderColor: chainStatus.failedAt?.Id === item.Id ? 'error.main' : undefined,
                  borderWidth: chainStatus.failedAt?.Id === item.Id ? 2 : undefined,
                  borderStyle: chainStatus.failedAt?.Id === item.Id ? 'solid' : undefined,
                  boxShadow: theme => theme.palette.mode === 'dark' 
                    ? '0 10px 25px -5px rgba(0, 0, 0, 0.4), 0 0 15px -3px var(--glow-indigo)' 
                    : '0 4px 6px rgba(0, 0, 0, 0.1)',
                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(30, 41, 59, 0.8)' : undefined,
                  position: 'relative',
                  overflow: 'hidden',
                  border: theme => theme.palette.mode === 'dark' ? '1px solid rgba(99, 102, 241, 0.3)' : undefined,
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: theme => theme.palette.mode === 'dark' 
                      ? '0 12px 28px rgba(0, 0, 0, 0.25), 0 0 15px var(--glow-indigo)' 
                      : '0 6px 12px rgba(0, 0, 0, 0.15)',
                  },
                  '&::after': theme => theme.palette.mode === 'dark' ? {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '2px',
                    background: 'linear-gradient(90deg, var(--accent-indigo), var(--accent-blue), var(--accent-purple), var(--accent-pink))',
                    backgroundSize: '200% 200%',
                    animation: 'gradientShift 4s ease infinite',
                  } : {},
                  // Add index marker on left side
                  '&::before': theme => theme.palette.mode === 'dark' ? {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '4px',
                    height: '100%',
                    background: `linear-gradient(to bottom, var(--accent-indigo), var(--accent-${index % 2 ? 'blue' : 'purple'}))`,
                    zIndex: 2,
                  } : {},
                  // Add chain-card style conditionally
                  ...(darkMode ? {
                    '&.chain-card': {
                      position: 'relative',
                    },
                    '&.chain-card::before': index > 0 ? {
                      content: '""',
                      position: 'absolute',
                      top: '-15px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '2px',
                      height: '15px',
                      background: 'linear-gradient(to bottom, var(--accent-blue), var(--accent-indigo))',
                      zIndex: 3,
                    } : {},
                    '&.chain-card::after': index > 0 ? {
                      content: '""',
                      position: 'absolute',
                      top: '-15px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      backgroundColor: 'var(--accent-indigo)',
                      zIndex: 4,
                      boxShadow: '0 0 10px var(--glow-indigo)',
                      animation: 'pulse 2s infinite ease-in-out',
                    } : {},
                  } : {}),
                }}
                className={darkMode ? 'chain-card' : ''}
              >
                <CardHeader 
                  sx={{
                    backgroundColor: theme => theme.palette.mode === 'dark' 
                      ? 'rgba(15, 23, 42, 0.6)' 
                      : theme.palette.grey[50],
                    borderBottom: '1px solid',
                    borderColor: theme => theme.palette.mode === 'dark' 
                      ? 'rgba(71, 85, 105, 0.3)' 
                      : 'divider',
                  }}
                  title={
                    <Icon 
                      item={item} 
                      previousItem={items[index + 1]} 
                    />
                  } 
                />
                <CardContent 
                  sx={{ 
                    p: 3,
                    backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(30, 41, 59, 0.6)' : undefined,
                    position: 'relative',
                  }}
                >
                  {/* Additional visual chain indicator at top of content */}
                  <Box 
                    sx={{
                      display: theme => theme.palette.mode === 'dark' ? 'block' : 'none',
                      position: 'absolute',
                      top: 0,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '40px',
                      height: '2px',
                      background: 'linear-gradient(to right, var(--accent-indigo), var(--accent-blue))',
                      opacity: 0.7,
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        top: '-3px',
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: 'var(--accent-blue)',
                        boxShadow: '0 0 8px var(--glow-blue)',
                      },
                      '&.chain-dot': {
                        animation: 'dotPulse 3s infinite ease-in-out, glowPulse 4s infinite ease-in-out',
                      },
                    }}
                    className={darkMode ? 'chain-dot' : ''}
                  />
                  
                  <Box mb={2}>
                    <Typography 
                      variant="subtitle2" 
                      color="textSecondary" 
                      gutterBottom 
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: theme => theme.palette.mode === 'dark' ? '#94a3b8' : undefined,
                      }}
                    >
                      <ArticleOutlinedIcon 
                        fontSize="small" 
                        sx={{ 
                          opacity: 0.7,
                          color: theme => theme.palette.mode === 'dark' ? 'var(--accent-blue)' : undefined,
                          animation: theme => theme.palette.mode === 'dark' ? 'pulse 3s infinite ease-in-out' : 'none'
                        }} 
                      />
                      Ethereum Hash
                    </Typography>
                    <Box 
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        backgroundColor: theme => theme.palette.mode === 'dark' 
                          ? 'rgba(15, 23, 42, 0.6)' 
                          : theme.palette.grey[50],
                        p: 1,
                        borderRadius: 1,
                        transition: 'all 0.3s ease',
                        border: theme => theme.palette.mode === 'dark' ? '1px solid rgba(71, 85, 105, 0.3)' : 'none',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': {
                          backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.8)' : undefined,
                          transform: theme => theme.palette.mode === 'dark' ? 'translateY(-2px)' : 'none',
                          boxShadow: theme => theme.palette.mode === 'dark' ? '0 4px 12px rgba(0, 0, 0, 0.2)' : 'none',
                        },
                        '&::before': theme => theme.palette.mode === 'dark' ? {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '30%',
                          height: '100%',
                          background: 'linear-gradient(90deg, rgba(59, 130, 246, 0.1), transparent)',
                          zIndex: 0,
                          pointerEvents: 'none',
                        } : {},
                      }}
                    >
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontFamily: 'monospace',
                          flexGrow: 1,
                          wordBreak: 'break-all',
                          color: theme => theme.palette.mode === 'dark' ? '#60a5fa' : undefined,
                          position: 'relative',
                          zIndex: 1,
                          textShadow: theme => theme.palette.mode === 'dark' ? '0 0 2px rgba(96, 165, 250, 0.2)' : 'none',
                        }}
                      >
                        {item.EthereumHash}
                      </Typography>
                      <Tooltip title="Copy hash">
                        <IconButton 
                          size="small"
                          onClick={() => handleCopy(item.EthereumHash)}
                          sx={{ 
                            backgroundColor: theme => theme.palette.mode === 'dark' 
                              ? 'rgba(30, 41, 59, 0.8)' 
                              : 'white',
                            color: theme => theme.palette.mode === 'dark' ? '#94a3b8' : undefined,
                            position: 'relative',
                            zIndex: 1,
                            transition: 'all 0.2s ease',
                            '&:hover': { 
                              backgroundColor: theme => theme.palette.mode === 'dark' 
                                ? 'rgba(51, 65, 85, 0.9)' 
                                : theme.palette.grey[200],
                              transform: theme => theme.palette.mode === 'dark' ? 'scale(1.1)' : undefined,
                              boxShadow: theme => theme.palette.mode === 'dark' ? '0 0 8px var(--glow-blue)' : 'none',
                            }
                          }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  
                  <Box mb={2}>
                    <Typography 
                      variant="subtitle2" 
                      color="textSecondary" 
                      gutterBottom
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: theme => theme.palette.mode === 'dark' ? '#94a3b8' : undefined,
                      }}
                    >
                      <LinkOutlinedIcon 
                        fontSize="small" 
                        sx={{ 
                          opacity: 0.7,
                          color: theme => theme.palette.mode === 'dark' ? 'var(--accent-amber)' : undefined,
                          animation: theme => theme.palette.mode === 'dark' ? 'pulse 3s infinite ease-in-out' : 'none'
                        }} 
                      />
                      Bitcoin Hash
                    </Typography>
                    <Box 
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        backgroundColor: theme => theme.palette.mode === 'dark' 
                          ? 'rgba(15, 23, 42, 0.6)' 
                          : theme.palette.grey[50],
                        p: 1,
                        borderRadius: 1,
                        transition: 'all 0.3s ease',
                        border: theme => theme.palette.mode === 'dark' ? '1px solid rgba(71, 85, 105, 0.3)' : 'none',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': {
                          backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.8)' : undefined,
                          transform: theme => theme.palette.mode === 'dark' ? 'translateY(-2px)' : 'none',
                          boxShadow: theme => theme.palette.mode === 'dark' ? '0 4px 12px rgba(0, 0, 0, 0.2)' : 'none',
                        },
                        '&::before': theme => theme.palette.mode === 'dark' ? {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '30%',
                          height: '100%',
                          background: 'linear-gradient(90deg, rgba(234, 179, 8, 0.1), transparent)',
                          zIndex: 0,
                          pointerEvents: 'none',
                        } : {},
                      }}
                    >
                      <Link
                        href={getExplorerUrl('bitcoin', item.BitcoinHash, network)}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="none"
                        onClick={(e) => {
                          if (!item.BitcoinHash) {
                            e.preventDefault();
                          }
                        }}
                        sx={{
                          fontFamily: 'monospace',
                          color: theme => theme.palette.mode === 'dark' ? '#fbbf24' : 'primary.main',
                          flexGrow: 1,
                          wordBreak: 'break-all',
                          position: 'relative',
                          zIndex: 1,
                          textShadow: theme => theme.palette.mode === 'dark' ? '0 0 2px rgba(251, 191, 36, 0.2)' : 'none',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            color: theme => theme.palette.mode === 'dark' ? '#fcd34d' : undefined,
                            textDecoration: 'none',
                          }
                        }}
                      >
                        {item.BitcoinHash}
                      </Link>
                      <Tooltip title="Copy hash">
                        <IconButton 
                          size="small"
                          onClick={() => handleCopy(item.BitcoinHash)}
                          sx={{ 
                            backgroundColor: theme => theme.palette.mode === 'dark' 
                              ? 'rgba(30, 41, 59, 0.8)' 
                              : 'white',
                            color: theme => theme.palette.mode === 'dark' ? '#94a3b8' : undefined,
                            position: 'relative',
                            zIndex: 1,
                            transition: 'all 0.2s ease',
                            '&:hover': { 
                              backgroundColor: theme => theme.palette.mode === 'dark' 
                                ? 'rgba(51, 65, 85, 0.9)' 
                                : theme.palette.grey[200],
                              transform: theme => theme.palette.mode === 'dark' ? 'scale(1.1)' : undefined,
                              boxShadow: theme => theme.palette.mode === 'dark' ? '0 0 8px var(--glow-amber)' : 'none',
                            }
                          }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  <Box>
                    <Typography 
                      variant="subtitle2" 
                      color="textSecondary" 
                      gutterBottom
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: theme => theme.palette.mode === 'dark' ? '#94a3b8' : undefined,
                      }}
                    >
                      <AccessTimeIcon 
                        fontSize="small" 
                        sx={{
                          color: theme => theme.palette.mode === 'dark' ? 'var(--accent-teal)' : undefined,
                          animation: theme => theme.palette.mode === 'dark' ? 'pulse 3s infinite ease-in-out' : 'none'
                        }}
                      />
                      Timestamp
                    </Typography>
                    <Typography 
                      variant="body2"
                      sx={{
                        backgroundColor: theme => theme.palette.mode === 'dark' 
                          ? 'rgba(15, 23, 42, 0.6)' 
                          : theme.palette.grey[100],
                        color: theme => theme.palette.mode === 'dark' ? '#cbd5e1' : undefined,
                        p: 1,
                        borderRadius: 1,
                        border: theme => theme.palette.mode === 'dark' ? '1px solid rgba(71, 85, 105, 0.3)' : 'none',
                        transition: 'all 0.3s ease',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': {
                          backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.8)' : undefined,
                        },
                        '&::before': theme => theme.palette.mode === 'dark' ? {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '30%',
                          height: '100%',
                          background: 'linear-gradient(90deg, rgba(45, 212, 191, 0.1), transparent)',
                          zIndex: 0,
                          pointerEvents: 'none',
                        } : {},
                      }}
                    >
                      {item.UnixTimestamp 
                        ? new Date(item.UnixTimestamp * 1000).toLocaleString()
                        : new Date(item.Timedate).toLocaleString()
                      }
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          '& .MuiSnackbarContent-root': {
            backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(30, 41, 59, 0.9)' : undefined,
            color: theme => theme.palette.mode === 'dark' ? '#e2e8f0' : undefined,
            border: theme => theme.palette.mode === 'dark' ? '1px solid rgba(71, 85, 105, 0.3)' : undefined,
            boxShadow: theme => theme.palette.mode === 'dark' ? '0 4px 12px rgba(0, 0, 0, 0.2)' : undefined,
          }
        }}
      />
    </Box>
  );
};

export default DynamoDBData;
